import { Link } from 'react-router-dom';
import { Skeleton } from '@clarix/ui';
import { UserAvatar } from '@clarix/ui-shared';
import { useDirectoryManagerEdges, useDirectoryMember } from '@clarix/services-users';
import type { DirectoryMember } from '@clarix/shared-types';
import { SectionCard } from './ProfileSection';
import { roleLine } from './roleLine';

interface PersonRowProps {
  membershipId: string;
  /** Name from the edge / directory row, so the row stays useful if its own fetch fails. */
  name: string;
}

function PersonRow({ membershipId, name }: PersonRowProps) {
  const person = useDirectoryMember(membershipId);
  const subtitle = roleLine(person.data?.designation?.name, person.data?.department?.name);

  return (
    <Link
      to={`/profile/${membershipId}`}
      className="hover:bg-surface focus-visible:ring-ring flex items-center gap-3 rounded-md py-2 outline-none transition-colors focus-visible:ring-2"
    >
      <UserAvatar name={name} className="h-9 w-9 flex-none" />
      <div className="min-w-0 flex-1">
        <p className="text-foreground truncate text-xl font-medium">{name}</p>
        {person.isLoading ? (
          <Skeleton className="mt-1 h-4 w-32" />
        ) : (
          subtitle && <p className="text-muted-foreground truncate text-lg">{subtitle}</p>
        )}
      </div>
    </Link>
  );
}

interface ReportsToCardProps {
  /** The profile owner's directory row; null/undefined means no manager can be shown. */
  member: DirectoryMember | null | undefined;
  isLoading?: boolean;
}

/** Primary manager plus co-managers of a member; each row opens that person's profile. */
export function ReportsToCard({ member, isLoading = false }: ReportsToCardProps) {
  const edges = useDirectoryManagerEdges(member?.id ?? null);

  const primaryId = member?.manager?.membership_id;
  const coManagers = (edges.data ?? []).filter(
    (edge) =>
      edge.relationship_type === 'co_manager' && !edge.is_primary && edge.manager.id !== primaryId,
  );

  return (
    <SectionCard title="Reports to">
      {isLoading ? (
        <Skeleton className="h-12 w-full" />
      ) : member?.manager ? (
        <PersonRow membershipId={member.manager.membership_id} name={member.manager.name} />
      ) : (
        <p className="text-muted-foreground py-2.5 text-xl">No manager assigned</p>
      )}

      {!isLoading && edges.isLoading && <Skeleton className="mt-2 h-12 w-full" />}

      {coManagers.length > 0 && (
        <div className="border-border mt-2 border-t pt-3">
          <h3 className="text-muted-foreground mb-1 text-sm font-medium uppercase tracking-wide">
            Co-managers
          </h3>
          <ul>
            {coManagers.map((edge) => (
              <li key={edge.id}>
                <PersonRow membershipId={edge.manager.id} name={edge.manager.name} />
              </li>
            ))}
          </ul>
        </div>
      )}
    </SectionCard>
  );
}
